import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { getUserPermissions } from "src/utils/permissionsUtils";

const OVERLAY_TYPE_OPTIONS = [
  {
    value: "simple",
    label: "Simple",
    description:
      "Shows a skippable overlay prompting the user to complete their profile.",
  },
  {
    value: "forced",
    label: "Forced",
    description:
      "Shows a blocking overlay that requires profile completion before continuing.",
  },
];

const DEFAULT_SETTINGS = {
  showProfileCompletionOverlay: false,
  overlayType: "simple",
};

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getOverlayFields = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;

  if (
    data.alerts?.showProfileCompletionOverlay &&
    typeof data.alerts.showProfileCompletionOverlay === "object"
  ) {
    return data.alerts.showProfileCompletionOverlay;
  }

  if (
    data.showProfileCompletionOverlay &&
    typeof data.showProfileCompletionOverlay === "object"
  ) {
    return data.showProfileCompletionOverlay;
  }

  if (
    typeof data.showProfileCompletionOverlay === "boolean" ||
    typeof data.overlayType === "string"
  ) {
    return data;
  }

  return null;
};

const buildNextSettings = (currentSettings, enabled, overlayType) => {
  const overlay = {
    showProfileCompletionOverlay: enabled,
    overlayType,
  };

  if (
    currentSettings?.alerts &&
    typeof currentSettings.alerts.showProfileCompletionOverlay === "object"
  ) {
    return {
      ...currentSettings,
      alerts: {
        ...currentSettings.alerts,
        showProfileCompletionOverlay: {
          ...currentSettings.alerts.showProfileCompletionOverlay,
          ...overlay,
        },
      },
    };
  }

  if (
    currentSettings?.showProfileCompletionOverlay &&
    typeof currentSettings.showProfileCompletionOverlay === "object"
  ) {
    return {
      ...currentSettings,
      showProfileCompletionOverlay: {
        ...currentSettings.showProfileCompletionOverlay,
        ...overlay,
      },
    };
  }

  return {
    ...(currentSettings && typeof currentSettings === "object"
      ? currentSettings
      : {}),
    ...overlay,
  };
};

export default function EditProfileCompletionOverlayDialog({
  open,
  onClose,
  rawSettings,
  isSaving = false,
  onSave,
}) {
  const { isViewer } = getUserPermissions();
  const [enabled, setEnabled] = useState(
    DEFAULT_SETTINGS.showProfileCompletionOverlay
  );
  const [overlayType, setOverlayType] = useState(DEFAULT_SETTINGS.overlayType);
  const [currentSettings, setCurrentSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    if (!open) return;

    const parsed = tryParseJson(rawSettings);
    const overlay = getOverlayFields(parsed) || DEFAULT_SETTINGS;

    setCurrentSettings(
      parsed && typeof parsed === "object" ? parsed : DEFAULT_SETTINGS
    );
    setEnabled(Boolean(overlay.showProfileCompletionOverlay));
    setOverlayType(overlay.overlayType === "forced" ? "forced" : "simple");
  }, [open, rawSettings]);

  const controlsDisabled = isViewer || isSaving;

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = () => {
    onSave(
      JSON.stringify(
        buildNextSettings(currentSettings, enabled, overlayType),
        null,
        2
      )
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Profile Completion Overlay</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={controlsDisabled}
                  color={enabled ? "success" : "primary"}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {enabled ? "Enabled" : "Disabled"}
                </Typography>
              }
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", ml: 1 }}
            >
              When enabled, users with an incomplete profile will see the
              selected overlay.
            </Typography>
          </Box>

          <FormControl fullWidth disabled={!enabled || controlsDisabled}>
            <InputLabel id="overlay-type-label">Overlay Type</InputLabel>
            <Select
              labelId="overlay-type-label"
              value={overlayType}
              label="Overlay Type"
              onChange={(event) => setOverlayType(event.target.value)}
              renderValue={(value) =>
                OVERLAY_TYPE_OPTIONS.find((option) => option.value === value)
                  ?.label ?? value
              }
            >
              {OVERLAY_TYPE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ whiteSpace: "normal", alignItems: "flex-start" }}
                >
                  <ListItemText
                    primary={option.label}
                    secondary={option.description}
                    primaryTypographyProps={{ fontWeight: 600 }}
                    secondaryTypographyProps={{ sx: { whiteSpace: "normal" } }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          autoFocus
          disabled={controlsDisabled}
          startIcon={
            isSaving ? <CircularProgress size={16} thickness={6} /> : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
